import { ApiError } from '@/api/client/http';

/**
 * What a catalogue write refusal SAYS.
 *
 * ---------------------------------------------------------------------------
 * THE API'S CODES ARE THE VOCABULARY
 * ---------------------------------------------------------------------------
 * The taxonomy slice has a closed set of refusal codes, and the console maps
 * them to one plain sentence each. The mapping is by CODE and never by status:
 * a 409 carries four different meanings here — a duplicate slug, a duplicate
 * attribute key, an invalid parent and a stale draft — and a merchant cannot
 * act on "conflict".
 *
 * A code with no binding keeps the server's own message: the server said it,
 * and the console has nothing better to offer.
 */
const REFUSAL_WORDS: Readonly<Record<string, string>> = {
  CATEGORY_SLUG_CONFLICT: 'A category with that slug already exists.',
  ATTRIBUTE_KEY_CONFLICT: 'An attribute with that key already exists.',
  CATEGORY_PARENT_INVALID: 'That parent category cannot be used.',
  CATEGORY_DEPTH_EXCEEDED: 'That would be deeper than the category tree allows.',
  CATEGORY_NOT_ENABLED: 'That category is not enabled.',
  CATEGORY_ARCHIVED: 'That category is archived.',
  VERSION_CONFLICT: 'The category changed since this page loaded, so nothing was written.',
  INVALID_CATEGORY: 'The API refused those category details.',
  INVALID_CATEGORY_ATTRIBUTE_SET: 'The API refused that attribute set.',
  INVALID_ATTRIBUTE_DEFINITION: 'The API refused those attribute details.',
  IDEMPOTENCY_KEY_REQUIRED: 'The request was missing its idempotency key.',
  IDEMPOTENCY_KEY_REUSED: 'That idempotency key was already used for a different request.',
  FORBIDDEN: 'Not permitted for your role.',
};

/** One sentence about why a catalogue write was refused. */
export function describeTaxonomyFailure(error: Error): string {
  if (error instanceof ApiError) {
    const words = REFUSAL_WORDS[error.code];
    if (words !== undefined) return words;
    return error.message;
  }
  return error.message;
}
